import { Car } from "@/types/fleet";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Settings, Infinity, Banknote, MessageCircle } from "lucide-react";
import { trackViewCar, trackOpenDetails, trackOpenBooking, trackClickWhatsApp } from "@/utils/analytics";
import { buildViewedCarMessage, buildGeneralWhatsAppLink } from "@/utils/whatsapp";

interface DASFleetCardProps {
  car: Car;
  onRentClick: (car: Car) => void;
  onDetailsClick: (car: Car) => void;
}

export const DASFleetCard = ({ car, onRentClick, onDetailsClick }: DASFleetCardProps) => {
  const handleRentClick = () => {
    trackOpenBooking(car.id);
    onRentClick(car);
  };

  const handleDetailsClick = () => {
    trackOpenDetails(car.id);
    onDetailsClick(car);
  };

  const handleQuickWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    trackClickWhatsApp(`quick_inquiry_${car.id}`);
    const message = buildViewedCarMessage(car.title);
    window.open(buildGeneralWhatsAppLink(message), '_blank');
  };

  return (
    <Card className="group overflow-hidden transition-all duration-300 hover:shadow-card-hover">
      <div className="relative h-56 overflow-hidden bg-gray-100">
        <img 
          src={car.image_hero} 
          alt={car.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
          decoding="async"
          width="400"
          height="224"
        />
        {car.price_per_day_chf > 0 && (
          <Badge className="absolute top-4 right-4 bg-gold text-white font-bold text-base px-3 py-1">
            CHF {car.price_per_day_chf}.- / 24 h
          </Badge>
        )}
        {car.price_per_day_chf === 0 && (
          <Badge className="absolute top-4 right-4 bg-secondary text-white font-bold text-base px-3 py-1">
            Preis auf Anfrage
          </Badge>
        )}
      </div>
      
      <CardContent className="p-6">
        <h3 className="text-2xl font-heading font-bold text-black mb-2">
          {car.title}
        </h3>
        <p className="text-sm text-gray-700 mb-4">{car.description}</p>
        
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge variant="secondary" className="bg-gray-100 text-gray-700 hover:bg-gray-200 pointer-events-none">
            <Infinity className="h-3 w-3 mr-1" />
            KM ∞
          </Badge>
          <Badge variant="secondary" className="bg-gray-100 text-gray-700 hover:bg-gray-200 pointer-events-none">
            <Banknote className="h-3 w-3 mr-1" />
            Kaution {car.deposit_chf}
          </Badge>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm text-gray-700">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gold" />
            <span>{car.seats} {typeof car.seats === 'string' ? 'Personen' : 'Sitze'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-gold" />
            <span>{car.transmission}</span>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="p-6 pt-0 flex gap-2">
        <Button 
          className="flex-1 bg-gold hover:bg-gold-hover active:bg-gold-hover text-white font-semibold transition-colors duration-200 touch-manipulation"
          onClick={handleRentClick}
        >
          Mieten
        </Button>
        <Button 
          variant="outline"
          size="icon"
          className="border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white transition-colors duration-200 touch-manipulation"
          onClick={handleQuickWhatsApp}
          title="Direkt auf WhatsApp fragen"
        >
          <MessageCircle className="h-4 w-4" />
        </Button>
        <Button 
          variant="outline"
          className="border-2 border-gold text-gold hover:bg-gold hover:text-white active:bg-gold active:text-white font-semibold transition-colors duration-200 touch-manipulation"
          onClick={handleDetailsClick}
        >
          Details
        </Button>
      </CardFooter>
    </Card>
  );
};
